// Signup.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from '../styles/Auth.module.css';
import { useAuth } from '../context/AuthContext';
import { VideoSessionIcon, ProgressTrackingIcon, SaveHistoryIcon } from '../components/Icons/FeatureIcons';
import GoogleAuthButton from '../components/GoogleAuthButton/GoogleAuthButton';
import { useDocumentHead } from '../hooks/useDocumentHead';

const Signup = () => {
  useDocumentHead(
    'Create Account | TopSpeaks',
    'Join TopSpeaks to transform your speaking skills with AI-powered feedback, expert coaching, and progress tracking. Start your journey to becoming a confident and impactful speaker.',
    'index, follow'
  );
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  // eslint-disable-next-line
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const isFormValid = () => {
    return formData.fullName &&
      formData.email &&
      formData.password &&
      formData.confirmPassword &&
      formData.acceptTerms;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      await register(formData);
      // Instead of navigating to login, show verification message
      setVerificationSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (verificationSent) {
    return (
      <div className={styles.container}>
        <div className={styles.verificationMessage}>
          <h2>Check Your Email</h2>
          <p>We've sent a verification link to {formData.email}</p>
          <p>Please click the link in the email to complete your registration.</p>
          <small>Don't forget to check your spam folder!</small>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.brandSection}>
          <div className={styles.brandContent}>
            <h1>TopSpeaks</h1>
            <h2>Elevate Your Presence</h2>
            <p>Join thousands of professionals using AI-powered insights to improve spekaing skills.</p>
            <div className={styles.features}>
              <div className={styles.featureItem}>
                <span className={styles.featureIcon}><VideoSessionIcon /></span>
                <span>Book Video Sessions with Industry Experts</span>
              </div>
              <div className={styles.featureItem}>
                <span className={styles.featureIcon}><ProgressTrackingIcon /></span>
                <span>Track Progress Over Time</span>
              </div>
              <div className={styles.featureItem}>
                <span className={styles.featureIcon}><SaveHistoryIcon /></span>
                <span>Auto Save Presentations & Feedback History</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formContainer}>
            <h2>Create Account</h2>
            <p className={styles.formSubtitle}>Start your journey to presentation excellence</p>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.inputGroup}>
                <label>Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                />
              </div>

              <div className={styles.inputGroup}>
                <label>Work Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  required
                />
              </div>

              <div className={styles.inputGroup}>
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.inputGroup}>
                <label>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms" className={styles.termsLink}>Terms and Conditions</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className={styles.termsLink}>Privacy Policy</Link>
                  </span>
                </label>
              </div>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting || !isFormValid()}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className={styles.formFooter}>
              <p>
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </div>
            <div className={styles.divider}>or</div>
            <GoogleAuthButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;